import * as React from 'react';
import {useEffect, useRef} from 'react';
import {Navigate, Route, Routes, useLocation} from 'react-router-dom';
import {Breadcrumb, Breadcrumbs} from '~/components/Breadcrumps';
import {useMessage} from '~/components/Dialogs';
import {NavTabs} from '~/components/Tabs';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {Intent} from '~/lib/intent';
import {fetchSellerAccountInfo} from '~/modules/Clients/api';
import {useSession} from '~/modules/Profile/session';
import {DashboardAbility} from '~/modules/Dashboard/ability';
import {fromSearch} from '~/modules/Dashboard/lib/search';
import {Granularity, PerformanceType} from '~/modules/Dashboard/models/Performance';
import {ChartType, DashboardReducer, useDashboardReducer} from '~/modules/Dashboard/reducer';
import {AdTypesPerformanceView} from '~/modules/Dashboard/views/PerformanceView/AdTypesPerformanceView';
import {AgenciesPerformanceView} from '~/modules/Dashboard/views/PerformanceView/AgenciesPerformanceView';
import {CountriesPerformanceView} from '~/modules/Dashboard/views/PerformanceView/CountriesPerformanceView';
import {PayersPerformanceView} from '~/modules/Dashboard/views/PerformanceView/PayersPerformanceView';
import {PerformanceView} from '~/modules/Dashboard/views/PerformanceView/PerformanceView';
import {PlatformsPerformanceView} from '~/modules/Dashboard/views/PerformanceView/PlatrormsPerformanceView';
import {SellersPerformanceView} from '~/modules/Dashboard/views/PerformanceView/SellersPerformanceView';
import {AgencySwitcher} from '~/views/HeaderView/AgencySwitcher';
import {dashboardLinks} from '../../links';
import style from './index.module.scss';
import { endOfDay, startOfDay } from 'date-fns';

export function DashboardViewInner() {
    const ability = useAbility(DashboardAbility);
    const message = useMessage();
    const {session} = useSession();
    const {fetchData, setPerformanceType, setAgencyId} = useDashboardReducer();
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setAgencyId(session.selectedAgencyId);
        fetchData({agencyId: session.selectedAgencyId});
    }, [session.selectedAgencyId]);
    const showMessage = (text: string, options: Record<string, any> = {}) => {
        message(text, {
            intent: Intent.DANGER,
            icon: <i className="fa fa-exclamation" />,
            ...options,
        });
    };
    useEffect(() => {
        if (ability.isSelfServe() || ability.isSeller()) {
            fetchSellerAccountInfo(ability.sellerId()).then(seller => {
                console.log({seller})
                if (!seller.isActive) {
                    showMessage('Please add your payment and tax info to start monetizing.', {
                        onOk: () => {
                            if (!seller.companyEmail) {
                                setTimeout(() => {
                                    showMessage('Please add your email.');
                                }, 100);
                            }
                        }
                    });
                } else if (!seller.companyEmail) {
                   showMessage('Please add your email.');
                }
            });
        }
    }, [ability.sellerId()]);
    return <div className={style.dashboard}>
        <Breadcrumb
            breadcrumb={{
                to: dashboardLinks.dashboard(),
                title: 'Dashboard'
            }}
        >
            <div className="page-header-sticky">
                <div className="page-header-breadcrumbs">
                    <Breadcrumbs/>
                </div>
                <Title className="page-header-title" toolbar={<AgencySwitcher />}>Dashboard</Title>
            </div>
            <NavTabs
                tabs={[
                    {
                        to: dashboardLinks.performanceByType(PerformanceType.SUMMARY),
                        children: 'Summary',
                        onClick: () => setPerformanceType(PerformanceType.SUMMARY)
                    },
                    {
                        to: dashboardLinks.performanceByType(PerformanceType.COUNTRY),
                        children: 'By Country',
                        onClick: () => setPerformanceType(PerformanceType.COUNTRY)
                    },
                    {
                        to: dashboardLinks.performanceByType(PerformanceType.AD_TYPE),
                        children: 'By AdType',
                        onClick: () => setPerformanceType(PerformanceType.AD_TYPE)
                    },
                    {
                        to: dashboardLinks.performanceByType(PerformanceType.PLATFORM),
                        children: 'By Platform',
                        onClick: () => setPerformanceType(PerformanceType.PLATFORM)
                    },
                    ability.canViewByPayer()
                        ? {
                            to: dashboardLinks.performanceByType(PerformanceType.PAYER),
                            children: 'By Payer',
                            onClick: () => setPerformanceType(PerformanceType.PAYER)
                        }
                        : null,
                    ability.canViewBySeller()
                        ? {
                            to: dashboardLinks.performanceByType(PerformanceType.SELLER),
                            children: 'By Seller',
                            onClick: () => setPerformanceType(PerformanceType.SELLER)
                        }
                        : null,
                    ability.canViewByAgency()
                        ? {
                            to: dashboardLinks.performanceByType(PerformanceType.AGENCY),
                            children: 'By Agency',
                            onClick: () => setPerformanceType(PerformanceType.AGENCY)
                        }
                        : null
                ].filter(tab => !!tab)}
            />
	            <Routes>
	                <Route path={PerformanceType.SUMMARY} element={<PerformanceView />} />
	                <Route path={PerformanceType.COUNTRY} element={<CountriesPerformanceView />} />
	                <Route path={PerformanceType.AD_TYPE} element={<AdTypesPerformanceView />} />
	                {ability.canViewByPayer() && (
	                    <Route path={PerformanceType.PAYER} element={<PayersPerformanceView />} />
	                )}
	                {ability.canViewBySeller() && (
	                    <Route path={PerformanceType.SELLER} element={<SellersPerformanceView />} />
	                )}
	                {ability.canViewByAgency() && (
                    <Route path={PerformanceType.AGENCY} element={<AgenciesPerformanceView />} />
                )}
                <Route path={PerformanceType.PLATFORM} element={<PlatformsPerformanceView />} />
                <Route
                    path="*"
                    element={
                        <Navigate
                            to={dashboardLinks.performanceByType(PerformanceType.SUMMARY)}
                            replace
                        />
                    }
                />
            </Routes>
        </Breadcrumb>
    </div>;
}

export function DashboardView() {
    const location = useLocation();
    const {session} = useSession();
    const [, , performanceTypeFromPath] = location.pathname.split('/');
    const performanceType =
        (performanceTypeFromPath as PerformanceType) || PerformanceType.SUMMARY;

    return (
        <DashboardReducer
            performanceQuery={fromSearch(location.search, {
                from: startOfDay(new Date()),
                to: endOfDay(new Date()),
                granularity: Granularity.HOUR,
                chartType: ChartType.TOTAL,
                performanceType,
                agencyId: session.selectedAgencyId ?? undefined,
            })}
            children={<DashboardViewInner />}
        />
    );
}
